import type { MessageDetail } from "../shared/types";
import { defuseHtml, NO_DNS_PREFETCH } from "./defuse-html";
import { escapeHtml, formatAddress, formatFullDate, formatSize, textToHtml } from "./format";

/** A message to print, with its decrypted content when the reader has unlocked it. */
export type PrintEntry = {
  message: MessageDetail;
  content?: { html: string | null; text: string } | undefined;
  /** Remote images (through the image proxy) may load, as they do in the reader. */
  allowRemote: boolean;
};

// Each email's own HTML goes in a shadow root, so its styles can't restyle the headers or the
// other emails in a conversation.
const BODY_STYLE = `
  :host{display:block;color:#1f2937;font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;overflow-wrap:anywhere;}
  img{max-width:100%;height:auto;}
  pre{white-space:pre-wrap;}
  blockquote{margin:0 0 0 .5em;padding-left:1em;border-left:3px solid #d1d5db;color:#4b5563;}
  a{color:#2563eb;}
  table{max-width:100%;}
`;

const PAGE_STYLE = `
  @page{margin:16mm}
  html,body{margin:0;padding:0;background:#fff;color:#1f2937;}
  body{font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
  h1{font-size:20px;line-height:1.3;margin:0 0 4px}
  .count{font-size:12.5px;color:#6b7280;margin:0 0 16px}
  article+article{margin-top:22px;padding-top:18px;border-top:2px solid #e5e7eb}
  .meta{font-size:12.5px;line-height:1.6;color:#374151;margin:8px 0 0;break-inside:avoid}
  .meta b{color:#111827}
  .subject{font-size:15px;font-weight:600;margin:0 0 4px;color:#111827}
  hr{border:0;border-top:1px solid #d1d5db;margin:10px 0 14px}
`;

const bareSubject = (subject: string) => subject.replace(/^((re|fwd?|aw|wg)\s*:\s*)+/i, "").trim();

const headerFor = (message: MessageDetail, withSubject: boolean) => {
  const files = message.attachments.filter((attachment) => !attachment.inline);
  const line = (label: string, value: string) => `<b>${label}:</b> ${escapeHtml(value)}<br>`;
  return [
    withSubject ? `<p class="subject">${escapeHtml(message.subject || "(no subject)")}</p>` : "",
    '<p class="meta">',
    line("From", formatAddress(message.from)),
    message.to.length ? line("To", message.to.map(formatAddress).join(", ")) : "",
    message.cc.length ? line("Cc", message.cc.map(formatAddress).join(", ")) : "",
    `<b>Date:</b> ${escapeHtml(formatFullDate(message.date))}`,
    files.length
      ? `<br><b>Attachments:</b> ${escapeHtml(files.map((file) => `${file.filename} (${formatSize(file.size)})`).join(", "))}`
      : "",
    "</p><hr>",
  ].join("");
};

const PROXIED = "/api/proxy";
const PROXIED_CSS = /url\s*\([^)]*\/api\/proxy[^)]*\)/gi;

/** An email's HTML without the images it loads through the image proxy. */
const withoutProxied = (html: string): string => {
  if (!html.includes(PROXIED)) return html;
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  for (const element of doc.body.querySelectorAll("*")) {
    for (const name of ["src", "srcset", "background", "poster"]) {
      if (element.getAttribute(name)?.includes(PROXIED)) element.removeAttribute(name);
    }
    const style = element.getAttribute("style");
    if (style?.includes(PROXIED)) element.setAttribute("style", style.replace(PROXIED_CSS, "none"));
    if (element.tagName === "STYLE" && element.textContent?.includes(PROXIED))
      element.textContent = element.textContent.replace(PROXIED_CSS, "none");
  }
  return doc.body.innerHTML;
};

/**
 * The printable page for one email or a whole conversation: the page itself, and each email's
 * body to put in its placeholder.
 */
export const buildPrintDocument = (
  entries: PrintEntry[],
  origin: string,
): { page: string; bodies: string[] } => {
  const subject = entries[0]?.message.subject || "(no subject)";
  const conversation = entries.length > 1;
  const articles = entries.map(
    ({ message }, index) =>
      // A reply whose subject drifted from the thread's keeps its own subject line.
      `<article>${headerFor(message, conversation && bareSubject(message.subject) !== bareSubject(subject))}<div data-print-body="${index}"></div></article>`,
  );
  // No scripts and nothing remote: images come only from attachments, and through the image
  // proxy when an email printed may show remote images; the others lose their proxied ones.
  const someRemote = entries.some((entry) => entry.allowRemote);
  const bodies = entries.map(({ message, content, allowRemote }) => {
    const source = content ?? message;
    const html =
      source.html !== null && someRemote && !allowRemote
        ? withoutProxied(source.html)
        : source.html;
    return `<style>${BODY_STYLE}</style>${html === null ? `<div style="white-space:pre-wrap">${textToHtml(source.text)}</div>` : defuseHtml(html)}`;
  });
  const proxy = someRemote ? ` ${origin}/api/proxy` : "";
  const csp = `default-src 'none'; img-src data: blob: ${origin}/api/attachments/${proxy}; style-src 'unsafe-inline'; font-src data:`;
  const page = [
    `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}">${NO_DNS_PREFETCH}`,
    // The title becomes the file name when saved as a PDF.
    `<title>${escapeHtml(subject)}</title><style>${PAGE_STYLE}</style></head><body>`,
    `<h1>${escapeHtml(subject)}</h1>`,
    conversation ? `<p class="count">${entries.length} messages</p>` : "",
    articles.join(""),
    "</body></html>",
  ].join("");
  return { page, bodies };
};

/**
 * Opens a clean, printable copy; the browser's print window can also save it as a PDF. The
 * window opens at once, while the click still lets it, and fills once `entries` are ready.
 */
export const printMessages = async (entries: PrintEntry[] | Promise<PrintEntry[]>) => {
  if (Array.isArray(entries) && entries.length === 0) return;
  const popup = window.open("", "_blank");
  if (!popup) return;
  popup.opener = null;
  let ready: PrintEntry[];
  try {
    ready = await entries;
  } catch (error) {
    popup.close();
    throw error;
  }
  const { page, bodies } = buildPrintDocument(ready, location.origin);
  popup.document.write(page);
  popup.document.close();
  const images: HTMLImageElement[] = [];
  for (const slot of popup.document.querySelectorAll<HTMLElement>("[data-print-body]")) {
    const root = slot.attachShadow({ mode: "open" });
    root.innerHTML = bodies[Number(slot.dataset.printBody)] ?? "";
    images.push(...root.querySelectorAll("img"));
  }
  popup.focus();
  // Print once the images are in, so they make it onto paper; slow ones get a few seconds.
  const loaded = images.map(
    (image) =>
      new Promise<void>((resolve) => {
        if (image.complete) return resolve();
        image.addEventListener("load", () => resolve(), { once: true });
        image.addEventListener("error", () => resolve(), { once: true });
      }),
  );
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, 4000));
  void Promise.race([Promise.all(loaded), timeout]).then(() => popup.print());
};
